import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Eye, Bell, Check, X, Lock, Download, FileSpreadsheet, FolderPlus, Send } from "lucide-react";

const OB_STATUS = {
  not_started: { label: "Not started", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  submitted: { label: "Submitted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function OnboardingCard({ proposalId }) {
  const [ob, setOb] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState({});

  const load = () => api.get(`/client-onboarding/${proposalId}`).then((r) => setOb(r.data)).catch(() => setOb(null)).finally(() => setLoaded(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [proposalId]);

  const sendOnboarding = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${proposalId}/onboarding/send`); toast.success(data.delivered ? (data.resent ? "Onboarding form re-sent" : "Onboarding form sent") : "Onboarding prepared (email delivery not confirmed)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send onboarding"); }
    finally { setBusy(false); }
  };

  if (!loaded) return null;

  if (!ob) return (
    <Card className="p-5 rounded-xl" data-testid="onboarding-card-empty">
      <div className="flex items-center gap-2 mb-2"><ClipboardCheck className="w-5 h-5 text-cass-teal" /><h3 className="font-alt font-semibold text-cass-navy">Client Onboarding</h3></div>
      <p className="text-sm text-cass-slate mb-4">No onboarding has been sent for this client yet. Send the secure onboarding form now — the client completes it from their existing link.</p>
      <Button size="sm" className="bg-cass-navy text-white" onClick={sendOnboarding} disabled={busy} data-testid="onboarding-send"><Send className="w-4 h-4 mr-1" />Send onboarding form</Button>
    </Card>
  );

  const st = OB_STATUS[ob.status] || OB_STATUS.not_started;
  const secTitle = (k) => (ob.sections || []).find((s) => s.key === k)?.title || k;
  const bySection = {};
  for (const q of ob.questions || []) (bySection[q.section] ||= []).push(q);
  const pending = (ob.pending_changes || []).filter((c) => c.decision === "pending");

  const reveal = async (q, index, subfield) => {
    try {
      const { data } = await api.post(`/client-onboarding/${ob.id}/reveal`, { key: q.key, index, subfield });
      setRevealed((r) => ({ ...r, [`${q.key}:${index ?? ""}:${subfield ?? ""}`]: data.value }));
    } catch (e) { toast.error(e.response?.data?.detail || "Not permitted"); }
  };
  const decide = async (key, decision) => {
    try { await api.post(`/client-onboarding/${ob.id}/change-decision`, { key, decision }); toast.success(decision === "accept" ? "Change accepted" : "Change rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const saveToClient = async () => {
    try { await api.post(`/clients/${ob.client_id}/onboarding-report`); toast.success("Saved to client record (PDF + Excel)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const remind = async () => {
    try { const { data } = await api.post(`/client-onboarding/${ob.id}/remind`); toast.success(data.delivered ? "Reminder sent" : "Reminder queued (delivery not confirmed)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };

  const showVal = (q, val, index, subfield) => {
    const rk = `${q.key}:${index ?? ""}:${subfield ?? ""}`;
    if (revealed[rk] !== undefined) return <span className="text-cass-navy font-medium">{String(revealed[rk])}</span>;
    const masked = (val === undefined || val === null || val === "");
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-cass-navy font-medium">{masked ? "—" : String(val)}</span>
        {!masked && q.sensitive && ob.can_reveal && (
          <button type="button" onClick={() => reveal(q, index, subfield)} className="text-cass-teal hover:underline text-xs inline-flex items-center gap-0.5" data-testid={`onb-reveal-${q.key}${subfield ? "-" + subfield : ""}`}><Eye className="w-3 h-3" />Reveal</button>
        )}
      </span>
    );
  };

  const STAGES = [
    { key: "not_started", label: "Invited" },
    { key: "in_progress", label: "In progress" },
    { key: "submitted", label: "Submitted" },
  ];
  const curStage = Math.max(0, STAGES.findIndex((x) => x.key === ob.status));
  const fmtD = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
  const openedAt = (ob.activity || []).find((a) => a.event === "opened")?.at;
  const stageDates = [ob.invite_sent_at || ob.created_at, openedAt, ob.submitted_at];
  const Stepper = ({ withDates = false }) => (
    <div className="flex items-start" data-testid="onboarding-stepper">
      {STAGES.map((s, i) => (
        <div key={s.key} className={`flex items-start ${i < STAGES.length - 1 ? "flex-1" : ""}`}>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${i < curStage ? "bg-cass-teal" : i === curStage ? "bg-cass-navy ring-4 ring-cass-navy/10" : "bg-slate-300"}`} />
              <span className={`text-[11px] font-medium whitespace-nowrap ${i <= curStage ? "text-cass-navy" : "text-cass-slate/50"}`}>{s.label}</span>
            </div>
            {withDates && <span className="text-[10px] text-cass-slate whitespace-nowrap pl-4" data-testid={`onboarding-stage-date-${s.key}`}>{fmtD(stageDates[i])}</span>}
          </div>
          {i < STAGES.length - 1 && <span className={`h-px flex-1 mx-2 mt-1 ${i < curStage ? "bg-cass-teal" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Card className="p-5 rounded-xl cursor-pointer hover:border-cass-teal hover:shadow-sm transition-all" data-testid="onboarding-card" role="button" tabIndex={0}
        onClick={() => setOpen(true)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-cass-teal" /><h3 className="font-alt font-semibold text-cass-navy">Client Onboarding</h3></div>
          <Badge variant="outline" className={st.cls} data-testid="onboarding-status">{st.label}{ob.progress ? ` · ${ob.progress.complete}/${ob.progress.total}` : ""}</Badge>
        </div>
        <Stepper />
        <div className="flex items-center justify-between gap-2 mt-3">
          <p className="text-xs text-cass-slate">Click to view onboarding details</p>
          {pending.length > 0 && <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200" data-testid="onboarding-review-flag">{pending.length} change{pending.length > 1 ? "s" : ""} to review</Badge>}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-cass-teal" />Client Onboarding{ob.client_name ? ` — ${ob.client_name}` : ""}</DialogTitle>
            <DialogDescription>Current stage and submitted details for this client's onboarding.</DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-border bg-cass-bone/20 p-3"><Stepper withDates /></div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Badge variant="outline" className={st.cls}>{st.label}{ob.progress ? ` · ${ob.progress.complete}/${ob.progress.total} answered` : ""}</Badge>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {ob.status === "submitted" && ob.can_reveal && (
                <>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${API}/client-onboarding/${ob.id}/report.pdf`, "_blank")} data-testid="onboarding-report-pdf"><Download className="w-4 h-4 mr-1" />PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${API}/client-onboarding/${ob.id}/report.xlsx`, "_blank")} data-testid="onboarding-report-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
                  <Button size="sm" className="bg-cass-navy text-white" onClick={saveToClient} data-testid="onboarding-save-client"><FolderPlus className="w-4 h-4 mr-1" />Save to client</Button>
                </>
              )}
              {ob.status !== "submitted" && <Button size="sm" variant="outline" onClick={remind} data-testid="onboarding-remind"><Bell className="w-4 h-4 mr-1" />Remind</Button>}
            </div>
          </div>

          {pending.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="onboarding-changes">
              <div className="text-sm font-semibold text-amber-800 mb-2">Changes to held information — please review</div>
              <div className="space-y-2">
                {pending.map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-3 text-sm" data-testid={`onb-change-${c.key}`}>
                    <div className="min-w-0"><span className="text-cass-slate">{c.label}: </span><span className="line-through text-cass-slate/70">{c.old || "—"}</span> → <span className="text-cass-navy font-medium">{c.new}</span></div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" className="bg-emerald-600 text-white h-7" onClick={() => decide(c.key, "accept")} data-testid={`onb-accept-${c.key}`}><Check className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-200" onClick={() => decide(c.key, "reject")} data-testid={`onb-reject-${c.key}`}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ob.status === "submitted" ? (
            <div className="space-y-5" data-testid="onboarding-answers">
              {(ob.sections || []).filter((s) => s.key !== "review" && (bySection[s.key] || []).length).map((s) => (
                <div key={s.key}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-cass-teal mb-2 pb-1 border-b border-border">{secTitle(s.key)}</div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {(bySection[s.key] || []).map((q) => {
                      const v = ob.answers?.[q.key];
                      if (q.field_type === "people") {
                        if (!(Array.isArray(v) && v.length)) return null;
                        return (
                          <div key={q.key} className="sm:col-span-2">
                            <div className="text-[11px] uppercase tracking-wide text-cass-slate mb-1">{q.label}</div>
                            <div className="space-y-1">{v.map((p, i) => (
                              <div key={i} className="text-sm text-cass-navy flex flex-wrap gap-x-4 gap-y-0.5">
                                <span className="font-medium">{p.name}</span>
                                {p.utr && <span className="text-cass-slate">UTR: {showVal(q, p.utr, i, "utr")}</span>}
                                {p.ni_number && <span className="text-cass-slate">NI: {showVal(q, p.ni_number, i, "ni_number")}</span>}
                              </div>
                            ))}</div>
                          </div>
                        );
                      }
                      const wide = ["textarea", "address"].includes(q.field_type) || q.document;
                      const label = (
                        <div className="text-[11px] uppercase tracking-wide text-cass-slate mb-0.5 flex items-center gap-1">
                          {q.sensitive && <Lock className="w-3 h-3 shrink-0" />}{q.label}
                        </div>
                      );
                      const value = q.document ? ((ob.documents?.[q.key]?.filename) || "—") : showVal(q, v);
                      return (
                        <div key={q.key} className={wide ? "sm:col-span-2" : ""}>
                          {label}
                          <div className="text-sm text-cass-navy break-words leading-snug">{value}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-cass-slate">The client hasn't submitted their onboarding form yet.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
